// src/Pages/Auth/variants/DefaultNarrative.jsx
//
// The general pitch — what every visitor saw before variants existed.
// Extracted verbatim from SignIn.jsx; no copy or styling changes.
//
// Props:
//   compact  true  -> the mobile strip above the auth card
//            false -> the desktop left panel
//   ctx      landing context (unused here; other variants read it)
//
// Variants own their outer Box. SignIn owns the Fade and the Grid around it.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, Divider, Link,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  ArrowRightAlt as ArrowFlowIcon,
  SubdirectoryArrowRight as ArrowDownIcon,
  RateReview as ReviewIcon,
  Share as SocialIcon,
  WbSunny as WeatherIcon,
  QuestionAnswer as ChatIcon,
} from '@mui/icons-material';

// ─── Data ─────────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    Icon:    ReviewIcon,
    label:   'Review Responses',
    detail:  'A review lands. A draft reply is ready for you to post.',
    bg:      'rgba(46,125,50,0.08)',
    color:   '#2E7D32',
    border:  'rgba(46,125,50,0.2)',
  },
  {
    Icon:    SocialIcon,
    label:   'Social Media Posts',
    detail:  'Facebook, Instagram and LinkedIn — written and scheduled.',
    bg:      'rgba(2,136,209,0.08)',
    color:   '#0277BD',
    border:  'rgba(2,136,209,0.2)',
  },
  {
    Icon:    ChatIcon,
    label:   'Customer Questions',
    detail:  'The question customers keep asking becomes a published FAQ your profile chatbot answers 24/7.',
    bg:      'rgba(94,53,177,0.08)',
    color:   '#5E35B1',
    border:  'rgba(94,53,177,0.2)',
  },
];

const SIGNAL_FLOW = [
  {
    Icon:      ReviewIcon,
    signal:    'A 3-star review lands',
    signalAt:  '9:02pm',
    outcome:   'Reply drafted, live on Google',
    outcomeAt: '9:06pm',
    mobileOutcome: 'Reply live on Google by 9:06',
    color:     '#2E7D32',
    bg:        'rgba(46,125,50,0.08)',
    border:    'rgba(46,125,50,0.2)',
  },
  {
    Icon:      WeatherIcon,
    signal:    '94° all week',
    signalAt:  'Monday',
    outcome:   'Post written, scheduled to FB, IG, LinkedIn',
    outcomeAt: 'Tue 8am',
    mobileOutcome: 'Post scheduled to all three',
    color:     '#0277BD',
    bg:        'rgba(2,136,209,0.08)',
    border:    'rgba(2,136,209,0.2)',
  },
  {
    Icon:      ChatIcon,
    signal:    'Fourth person asks about parking',
    signalAt:  'this month',
    outcome:   'FAQ published, chatbot answers it 24/7',
    outcomeAt: 'Live',
    mobileOutcome: 'FAQ live, chatbot answers 24/7',
    color:     '#5E35B1',
    bg:        'rgba(94,53,177,0.08)',
    border:    'rgba(94,53,177,0.2)',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const TwoFeaturePills = ({ compact = false }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 1 : 1.5, justifyContent: compact ? 'center' : 'flex-start' }}>
    {FEATURES.map(({ label, Icon, bg, color, border }) => (
      <Chip
        key={label}
        icon={<Icon sx={{ fontSize: `${compact ? 13 : 15}px !important`, color: `${color} !important` }} />}
        label={label}
        size={compact ? 'small' : 'medium'}
        sx={{
          bgcolor: bg, color, fontWeight: 600,
          fontSize: compact ? 11 : 13,
          border: `1px solid ${border}`,
          py: compact ? 0.25 : 0.5,
          '& .MuiChip-icon': { ml: compact ? '6px' : '8px' },
        }}
      />
    ))}
  </Box>
);

const TwoFeatureCard = ({ compact = false }) => (
  <Box sx={{ p: compact ? 2 : 2.5, bgcolor: 'white', borderRadius: 2.5, border: '1px solid #E8EDF2', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
    <Typography variant="overline" sx={{ color: '#90A4AE', letterSpacing: 2.5, fontSize: 10, fontWeight: 700, display: 'block', mb: 2 }}>
      WHAT ARRIVES ALREADY DONE
    </Typography>

    {FEATURES.map(({ Icon, label, detail, bg, color, border }) => (
      <Box
        key={label}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 2,
          mb: 2, pb: 2,
          borderBottom: '1px dashed #E8EDF2',
          '&:last-child': { mb: 0, pb: 0, borderBottom: 'none' }
        }}
      >
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          bgcolor: bg, border: `1px solid ${border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <Icon sx={{ color, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={700} color="#0D2B6B" sx={{ mb: 0.25 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="#546E7A" sx={{ lineHeight: 1.5, display: 'block' }}>
            {detail}
          </Typography>
        </Box>
      </Box>
    ))}

    <Box sx={{ pt: 1.5, mt: 0.5, borderTop: '1px dashed #E8EDF2' }}>
      <Typography variant="caption" sx={{ color: '#455A7B', fontWeight: 600, fontSize: 11.5, lineHeight: 1.55, display: 'block', mb: 1.25 }}>
        It learns from every edit you make. The drafts sound more like your business each week — and that history doesn't transfer to another tool.
      </Typography>
      <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 700, fontSize: 11, display: 'block', mb: 0.5 }}>
        🚀 $19/mo launch pricing
      </Typography>
      <Typography variant="caption" sx={{ color: '#90A4AE', fontWeight: 500, fontSize: 11 }}>
        Regular $49/mo after launch · Free tier available
      </Typography>
    </Box>
  </Box>
);

const SignalFlowStrip = ({ compact = false }) => (
  <Box sx={{
    p: compact ? 1.75 : 2.25,
    bgcolor: 'white',
    borderRadius: 2.5,
    border: '1px solid #E8EDF2',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  }}>
    <Typography
      variant="overline"
      sx={{
        color: '#90A4AE', letterSpacing: compact ? 1.8 : 2.5,
        fontSize: compact ? 10 : 11, fontWeight: 700,
        display: 'block', mb: compact ? 1.5 : 1.75,
        textAlign: compact ? 'center' : 'left',
        lineHeight: 1.4,
      }}
    >
      NOBODY ASKED IT TO DO ANY OF THIS
    </Typography>

    {SIGNAL_FLOW.map(({ Icon, signal, signalAt, outcome, outcomeAt, mobileOutcome, color, bg, border }) => (
      <Box
        key={signal}
        sx={{
          pb: 1.4, mb: 1.4,
          borderBottom: '1px dashed #E8EDF2',
          '&:last-of-type': { pb: 0, mb: 0, borderBottom: 'none' },
          ...(compact ? {} : { display: 'flex', alignItems: 'center', gap: 1.5 }),
        }}
      >
        {!compact && (
          <Box sx={{
            width: 34, height: 34, borderRadius: 1.5,
            bgcolor: bg, border: `1px solid ${border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon sx={{ color, fontSize: 18 }} />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 0 }}>
            {compact && <Icon sx={{ color, fontSize: 15, flexShrink: 0 }} />}
            <Typography variant="caption" sx={{ color: '#78909C', fontSize: compact ? 12 : 12.5, lineHeight: 1.4 }}>
              {signal}
              <Box component="span" sx={{ color: '#B0BEC5' }}>
                {compact ? (signalAt ? `, ${signalAt}` : '') : ` · ${signalAt}`}
              </Box>
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: '#0D2B6B', fontWeight: 600,
              fontSize: compact ? 12.5 : 13.5,
              lineHeight: 1.45, mt: 0.4,
              pl: compact ? '23px' : 0,
              display: 'flex', alignItems: 'flex-start', gap: 0.6,
            }}
          >
            {compact
              ? <ArrowDownIcon sx={{ color, fontSize: 14, mt: '2px', flexShrink: 0 }} />
              : <ArrowFlowIcon sx={{ color, fontSize: 17, mt: '-1px', flexShrink: 0 }} />}
            {compact ? mobileOutcome : outcome}
          </Typography>
        </Box>

        {!compact && (
          <Box sx={{
            flexShrink: 0, px: 1.25, py: 0.4,
            borderRadius: 5, bgcolor: bg,
          }}>
            <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: 11 }}>
              {outcomeAt}
            </Typography>
          </Box>
        )}
      </Box>
    ))}

    <Box sx={{ mt: 1.4, pt: 1.2, borderTop: '1px dashed #E8EDF2' }}>
      <Typography
        variant="caption"
        sx={{
          color: '#455A7B', fontSize: compact ? 11.5 : 12,
          fontWeight: 500, lineHeight: 1.5, display: 'block',
          textAlign: compact ? 'center' : 'left',
        }}
      >
        Your entire job was tapping approve.
      </Typography>
    </Box>
  </Box>
);

const LearnMoreLink = ({ center = false }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ textAlign: center ? 'center' : 'left', mt: 1.5 }}>
      <Link
        component="button"
        onClick={() => navigate('/landing/business')}
        underline="none"
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          color: '#1976D2', fontWeight: 600, fontSize: 14,
          cursor: 'pointer', border: 'none', background: 'none',
          '&:hover': { color: '#1565C0', '& .arrow': { transform: 'translateX(3px)' } },
        }}
      >
        See how it works
        <ArrowIcon className="arrow" sx={{ fontSize: 16, transition: 'transform 0.2s ease' }} />
      </Link>
    </Box>
  );
};

// Secondary links block — identical content desktop and mobile, only alignment
// differs, so it lives here once rather than twice.
const SecondaryLinks = ({ center = false }) => {
  const navigate = useNavigate();
  const linkSx = { color: '#1976D2', fontWeight: 600, fontSize: 13, cursor: 'pointer', border: 'none', background: 'none', '&:hover': { color: '#1565C0' } };
  return (
    <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid rgba(13,43,107,0.1)', ...(center ? { textAlign: 'center' } : {}) }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
        Want to help shape Revfer?{' '}
        <Link component="button" onClick={() => navigate('/founders-circle')} underline="none" sx={linkSx}>
          Join the Founders Circle →
        </Link>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
        Looking for a trusted local business?{' '}
        <Link component="button" onClick={() => navigate('/landing/users')} underline="none" sx={linkSx}>
          Discover businesses trusted by your network →
        </Link>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        Are you a creator or agency?{' '}
        <Link component="button" onClick={() => navigate('/affiliates')} underline="none" sx={linkSx}>
          Join our partner program →
        </Link>
      </Typography>
    </Box>
  );
};

// ─── Variant ──────────────────────────────────────────────────────────────────

export default function DefaultNarrative({ compact = false }) {
  const navigate = useNavigate();

  if (compact) {
    return (
      <Box sx={{ width: '100%', mb: 3, px: { xs: 1, sm: 0 } }}>
        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          <Chip
            label="For restaurants, salons and every business that runs on reputation"
            size="small"
            clickable
            onClick={() => navigate('/landing/business')}
            sx={{
              mb: 1.5,
              bgcolor: 'rgba(21,101,192,0.08)',
              color: '#1565C0',
              fontWeight: 700,
              fontSize: 11,
              border: '1px solid rgba(21,101,192,0.18)',
              height: 'auto',
              py: 0.75,
              '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center' },
              cursor: 'pointer',
            }}
          />

          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: '#0D2B6B', lineHeight: 1.25, mb: 1.5, fontSize: { xs: '1.35rem', sm: '1.6rem' } }}>
            Your reviews get responses.<br />
            Your social media gets posted.<br />
            <Box component="span" sx={{ color: '#1976D2' }}>
              You get your evenings back.
            </Box>
          </Typography>

          <Typography variant="body2" sx={{ color: '#455A7B', lineHeight: 1.65, maxWidth: 420, mx: 'auto', fontSize: { xs: 13, sm: 14 }, mb: 2 }}>
            Revfer watches your reviews, your season, your customers' questions —
            and does the work before you ask. No prompt to write. You just approve.
          </Typography>
        </Box>

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <TwoFeaturePills compact={true} />
        </Box>

        <Box sx={{ mb: 2 }}><SignalFlowStrip compact={true} /></Box>

        <Box sx={{ display: 'flex', bgcolor: '#F0F7FF', borderRadius: 2, border: '1px solid #BBDEFB', mb: 2, overflow: 'hidden' }}>
          {[
            { value: '13,000+', label: 'signed up' },
            { value: '$19/mo',  label: 'launch price' },
            { value: '~10 min', label: 'a week to run it' },
          ].map(({ value, label }, i, arr) => (
            <React.Fragment key={label}>
              <Box sx={{ flex: 1, textAlign: 'center', py: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1565C0', lineHeight: 1, mb: 0.25, fontSize: '1rem' }}>{value}</Typography>
                <Typography variant="caption" sx={{ color: '#546E7A', fontWeight: 500, fontSize: 10 }}>{label}</Typography>
              </Box>
              {i < arr.length - 1 && <Divider orientation="vertical" flexItem sx={{ borderColor: '#BBDEFB' }} />}
            </React.Fragment>
          ))}
        </Box>

        <LearnMoreLink center={true} />
        <SecondaryLinks center={true} />
      </Box>
    );
  }

  return (
    <Box sx={{ pr: 5, pt: 2 }}>
      <Chip
        label="For restaurants, salons and every business that runs on reputation"
        size="small"
        clickable
        onClick={() => navigate('/landing/business')}
        sx={{
          mb: 3,
          bgcolor: 'rgba(21,101,192,0.08)',
          color: '#1565C0',
          fontWeight: 700,
          fontSize: 11,
          border: '1px solid rgba(21,101,192,0.18)',
          cursor: 'pointer',
          height: 'auto',
          py: 0.75,
          '& .MuiChip-label': { whiteSpace: 'normal' },
          '&:hover': { bgcolor: 'rgba(21,101,192,0.14)', borderColor: '#1565C0' }
        }}
      />

      <Typography variant="h2" component="h1" sx={{ fontWeight: 800, color: '#0D2B6B', lineHeight: 1.15, mb: 2.5, fontSize: { lg: '2.6rem' } }}>
        Your reviews get responses.<br />
        Your social media gets posted.<br />
        <Box component="span" sx={{ color: '#1976D2' }}>
          You get your evenings back.
        </Box>
      </Typography>

      <Typography variant="body1" sx={{ color: '#455A7B', lineHeight: 1.75, fontSize: '1rem', mb: 3.5 }}>
        Revfer watches what actually happens at your business — a review landing,
        a heatwave, the question customers keep asking — and starts the work itself.
        Nothing to prompt. Nothing to set up. You read what it drafted and tap
        approve: about 10 minutes a week instead of ten hours.
      </Typography>

      <Typography variant="overline" sx={{ color: '#90A4AE', letterSpacing: 2.5, fontSize: 11, fontWeight: 700, display: 'block', mb: 1.5 }}>
        IT STARTS THE WORK — YOU APPROVE IT
      </Typography>
      <Box sx={{ mb: 3.5 }}><TwoFeaturePills compact={false} /></Box>

      <Box sx={{ mb: 3 }}><SignalFlowStrip compact={false} /></Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, px: 2.5, py: 2, bgcolor: '#F0F7FF', borderRadius: 2.5, border: '1px solid #BBDEFB' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1565C0', lineHeight: 1, mb: 0.25 }}>13,000+</Typography>
          <Typography variant="caption" sx={{ color: '#546E7A', fontWeight: 500, fontSize: 11 }}>users signed up</Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ borderColor: '#BBDEFB' }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1565C0', lineHeight: 1, mb: 0.25 }}>$19/mo</Typography>
          <Typography variant="caption" sx={{ color: '#546E7A', fontWeight: 500, fontSize: 11 }}>launch pricing</Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ borderColor: '#BBDEFB' }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1565C0', lineHeight: 1, mb: 0.25 }}>~10 min</Typography>
          <Typography variant="caption" sx={{ color: '#546E7A', fontWeight: 500, fontSize: 11 }}>a week to run it</Typography>
        </Box>
      </Box>

      <TwoFeatureCard compact={false} />

      <LearnMoreLink center={false} />
      <SecondaryLinks center={false} />
    </Box>
  );
}